import classnames from 'classnames'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import React, { useState, useRef, useEffect, useMemo } from 'react'

import { FormattedPrice } from '@src/blocks/currency'
import Product from '@src/quince-site-common/src/domains/Product'
import { ISubscriptionPlan } from '@src/quince-site-common/src/domains/Subscription'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { Skeleton } from '..'
import { Image } from '../image'
import { ICON_CONSTANTS, ICON_CONSTANTS_MAP } from '../productForm/icon-constants'

import styles from './purchaseTypeSelector.module.scss'

export const PURCHASE_TYPES = {
  SUBSCRIBE: 'SUBSCRIBE',
  ONE_TIME: 'ONE_TIME',
} as const

export type PurchaseType = (typeof PURCHASE_TYPES)[keyof typeof PURCHASE_TYPES]

interface FrequencyOption {
  value: string
  label: string
  isRecommended?: boolean
}

export interface PurchaseTypeSelectorProps {
  subscriptionId: string | undefined
  value: PurchaseType
  onChange: (value: PurchaseType) => void
  subscribeLabel?: string
  oneTimeLabel?: string
  name?: string
  variantSubscriptionPlans: ISubscriptionPlan[]
  selectedPlanId: number | undefined
  onPlanIdChange: (planId: number | undefined) => void
  formattedSubscriptionPrice: string
  formattedFestivalOfferPrice?: string
  formattedSalePrice: string
  subscriptionFestivalSavingPercentage?: number
  isFromPdp?: boolean
  showNewPdpDesignV2?: boolean
  product: Product
}

function mapPlansToFrequencyOptions(plans: ISubscriptionPlan[]): FrequencyOption[] {
  return plans.map(plan => ({
    value: String(plan.planId),
    label: plan.frequency.displayLabel,
    isRecommended: plan.isDefault,
  }))
}

export function getDefaultPlanId(plans: ISubscriptionPlan[]): number | undefined {
  const defaultPlan = plans.find(plan => plan.isDefault)
  return defaultPlan?.planId ?? plans[0]?.planId
}

function getPlanDetailsByPlanId(plans: ISubscriptionPlan[], planId: number | undefined): ISubscriptionPlan | undefined {
  if (!planId) return undefined
  return plans.find(p => p.planId === planId)
}

const FrequencySelector = dynamic(
  () => import(/* webpackChunkName: "frequency-selector" */ '@lastbrand/post-order-web').then(module => ({ default: module.FrequencySelector })),
  {
    ssr: false,
  },
)

const PostOrderProviderWrapper = dynamic(
  () =>
    import(/* webpackChunkName: "post-order-provider-wrapper" */ '@src/quince-site-integrated/src/integrations/post-order-web/post-order-provider-wrapper').then(module => ({
      default: module.PostOrderProviderWrapper,
    })),
  {
    ssr: false,
  },
)

export const PurchaseTypeSelectorShimmer = ({
  showShimmer,
  isFromPdp,
  children,
  showNewPdpDesignV2,
}: {
  showShimmer: boolean
  isFromPdp: boolean
  showNewPdpDesignV2: boolean
  children: React.ReactNode
}) => {
  if (!showShimmer) return children

  return (
    <div className={classnames(
      styles.shimmerWrapper,
      isFromPdp && styles.adjustedPaddingForMobile,
      showNewPdpDesignV2 && styles.adjustedPaddingForMobile_newPdpV2
    )}>
      <div className={styles.shimmerCard}>
        <Skeleton height={124} fullWidth variant="RECTANGULAR" className={styles.customSkeletonWrapper} />
      </div>
      <div className={styles.shimmerCard}>
        <Skeleton height={42} fullWidth variant="RECTANGULAR" className={styles.customSkeletonWrapper} />
      </div>
    </div>
  )
}

const PurchaseTypeSelector = ({
  subscriptionId,
  isFromPdp,
  value,
  onChange,
  subscribeLabel,
  oneTimeLabel,
  name = 'purchase_type',
  variantSubscriptionPlans,
  selectedPlanId,
  onPlanIdChange,
  formattedSubscriptionPrice,
  subscriptionFestivalSavingPercentage,
  formattedFestivalOfferPrice,
  formattedSalePrice,
  showNewPdpDesignV2,
  product,
}: PurchaseTypeSelectorProps) => {
  const { t } = useTranslation('common')

  const frequencyOptions = mapPlansToFrequencyOptions(variantSubscriptionPlans)

  const [isFrequencySelectorOpen, setIsFrequencySelectorOpen] = useState(false)
  const previousPlanIdRef = useRef<number | undefined>(selectedPlanId)

  const selectedPlanDetails = useMemo(
    () => getPlanDetailsByPlanId(variantSubscriptionPlans, selectedPlanId),
    [variantSubscriptionPlans, selectedPlanId],
  )

  const handleSelect = (type: PurchaseType) => {
    onChange(type)
    trackOnPurchaseTypeChange(type)
  }

  const handleFrequencyLinkClick = () => {
    previousPlanIdRef.current = selectedPlanId
    setIsFrequencySelectorOpen(true)
  }

  const handleFrequencyChange = (planId: string) => {
    onPlanIdChange(Number(planId))
  }

  const handleFrequencySave = () => {
    const existingFrequency = getPlanDetailsByPlanId(variantSubscriptionPlans, previousPlanIdRef.current)?.frequency
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_SHIPPING_FREQUENCY_CHANGE, {
      entity_type: 'user_selected',
      context: {
        existing_frequency: `${existingFrequency?.interval} ${existingFrequency?.unit}`,
        updated_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        is_modal: !isFromPdp,
      },
    })
    previousPlanIdRef.current = selectedPlanId
    handleFrequencyClose()
  }

  const handleFrequencyClose = () => {
    onPlanIdChange(previousPlanIdRef.current)
    setIsFrequencySelectorOpen(false)
  }

  const trackOnPurchaseTypeChange = (purchaseType: PurchaseType) => {
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_RADIO_BUTTON_SELECTED, {
      entity_type: 'user_selected',
      context: {
        subscriptions_button: purchaseType === PURCHASE_TYPES.SUBSCRIBE,
        shipping_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        is_modal: !isFromPdp,
        price_data: {
          regular_price: formattedSalePrice,
          subscription_price: formattedSubscriptionPrice,
          one_time_purchase_price: formattedFestivalOfferPrice
            ? formattedFestivalOfferPrice
            : formattedSalePrice,
        },
        subscription_status: {
          subscription_id: subscriptionId ?? null,
        },
        product_properties: getProductPropertiesForTracking(product),
      },
    })
  }

  const isSubscribeSelected = value === PURCHASE_TYPES.SUBSCRIBE

  const subscribeDisplayPrice = formattedSubscriptionPrice
  const subscribeStrikethroughPrice = formattedSalePrice

  const oneTimeDisplayPrice = formattedFestivalOfferPrice ? formattedFestivalOfferPrice : formattedSalePrice

  const oneTimeStrikethroughPrice = formattedFestivalOfferPrice && formattedSalePrice ? formattedSalePrice : null

  useEffect(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_RADIO_BUTTON_SELECTED, {
      // eslint-disable-next-line i18next/no-literal-string
      entity_type: 'default',
      context: {
        subscriptions_button: isSubscribeSelected,
        shipping_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        is_modal: !isFromPdp,
        price_data: {
          regular_price: formattedSalePrice,
          subscription_price: formattedSubscriptionPrice,
          one_time_purchase_price: formattedFestivalOfferPrice ? formattedFestivalOfferPrice : formattedSalePrice,
        },
        subscription_status: {
          subscription_id: subscriptionId ?? null,
        },
        product_properties: getProductPropertiesForTracking(product),
      },
    })
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_SHIPPING_FREQUENCY_CHANGE, {
      // eslint-disable-next-line i18next/no-literal-string
      entity_type: 'default',
      context: {
        existing_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        updated_frequency: null,
        is_modal: !isFromPdp,
        product_properties: getProductPropertiesForTracking(product),
      },
    })
  }, [])

  return (
    <div
      className={classnames(
        styles.container,
        isFromPdp && styles.adjustedPaddingForMobile,
        showNewPdpDesignV2 && styles.adjustedPaddingForMobile_newPdpV2,
      )}
    >
      <label className={`${styles.optionCard} ${isSubscribeSelected ? styles.optionCardSelected : ''}`}>
        <div className={styles.optionHeader}>
          <input
            type="radio"
            name={name}
            value={PURCHASE_TYPES.SUBSCRIBE}
            checked={isSubscribeSelected}
            onChange={() => {
              handleSelect(PURCHASE_TYPES.SUBSCRIBE)
            }}
            className={styles.radio}
          />
          <span className={styles.optionLabel}>{subscribeLabel}</span>
          <div className={styles.priceContainer}>
            <span className={styles.strikethroughPrice}>
              <FormattedPrice amount={subscribeStrikethroughPrice} />
            </span>
            <span
              className={classnames(
                styles.currentPrice,
                showNewPdpDesignV2 && styles.currentPrice_newDesignV2,
                formattedFestivalOfferPrice && styles.festivalOfferPrice,
                showNewPdpDesignV2 && formattedFestivalOfferPrice && styles.festivalOfferPrice_newDesignV2,
              )}
            >
              <FormattedPrice amount={subscribeDisplayPrice} />
            </span>
          </div>
        </div>

        <div className={styles.expandedDetails}>
          {subscriptionFestivalSavingPercentage ? (
            <div className={styles.benefitItem}>
              <div className={styles.iconWrapper}>
                <Image
                  url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.DISCOUNT_ICON]}
                  title={ICON_CONSTANTS.DISCOUNT_ICON}
                  alt=""
                  className={styles.iconImage}
                />
              </div>
              <span className={styles.savingsText}>
                <span
                  className={classnames(
                    styles.savingsTextPromo,
                    showNewPdpDesignV2 && styles.savingsTextPromo_newDesignV2,
                  )}
                >
                  {t('save_on_every_recurring_order_promo_text', {
                    subscriptionPromoDiscount: subscriptionFestivalSavingPercentage,
                  })}
                </span>{' '}
                <span className={styles.savingsTextPromoContinuation}>
                  {t('save_on_every_recurring_order_promo_text_continuation', {
                    subscriptionDiscount: selectedPlanDetails?.benefits?.value,
                  })}
                </span>
              </span>
            </div>
          ) : (
            <div className={styles.benefitItem}>
              <div className={styles.iconWrapper}>
                <Image
                  url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.DISCOUNT_ICON_GREEN]}
                  title={ICON_CONSTANTS.DISCOUNT_ICON_GREEN}
                  alt=""
                  className={styles.iconImage}
                />
              </div>
              <span className={styles.savingsText}>
                {t('save_on_every_recurring_order', { subscriptionDiscount: selectedPlanDetails?.benefits?.value })}
              </span>
            </div>
          )}

          <div className={styles.benefitItem}>
            <div className={styles.iconWrapper}>
              <Image
                url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.EDIT_ICON]}
                title={ICON_CONSTANTS.EDIT_ICON}
                alt=""
                className={styles.iconImage}
              />
            </div>
            <span>{t('change_or_cancel_anytime')}</span>
          </div>
          <div className={styles.benefitItem}>
            <div className={styles.iconWrapper}>
              <Image
                url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.RECURRING_ICON]}
                title={ICON_CONSTANTS.RECURRING_ICON}
                alt=""
                className={styles.iconImage}
              />
            </div>
            <span>
              {t('ships_every_label')}{' '}
              <button
                type="button"
                className={styles.frequencyLink}
                onClick={() => {
                  if (value === PURCHASE_TYPES.ONE_TIME) {
                    handleSelect(PURCHASE_TYPES.SUBSCRIBE)
                  } else {
                    handleFrequencyLinkClick()
                  }
                }}
              >
                {`${selectedPlanDetails?.frequency.displayLabel} ${selectedPlanDetails?.isDefault ? t('freq_common') : ''}`}
              </button>
            </span>
          </div>
        </div>
      </label>

      <label className={`${styles.optionCard} ${!isSubscribeSelected ? styles.optionCardSelected : ''}`}>
        <div className={styles.optionHeader}>
          <input
            type="radio"
            name={name}
            value={PURCHASE_TYPES.ONE_TIME}
            checked={!isSubscribeSelected}
            onChange={() => {
              handleSelect(PURCHASE_TYPES.ONE_TIME)
            }}
            className={styles.radio}
          />
          <span className={styles.optionLabel}>{oneTimeLabel}</span>
          <div className={styles.priceContainer}>
            {oneTimeStrikethroughPrice && (
              <span className={styles.strikethroughPrice}>
                <FormattedPrice amount={oneTimeStrikethroughPrice} />
              </span>
            )}
            <span
              className={classnames(
                styles.currentPrice,
                showNewPdpDesignV2 && styles.currentPrice_newDesignV2,
                formattedFestivalOfferPrice && styles.festivalOfferPrice,
                showNewPdpDesignV2 && formattedFestivalOfferPrice && styles.festivalOfferPrice_newDesignV2,
              )}
            >
              <FormattedPrice amount={oneTimeDisplayPrice} />
            </span>
          </div>
        </div>
      </label>

      <PostOrderProviderWrapper>
        <FrequencySelector
          isOpen={isFrequencySelectorOpen}
          title={t('choose_frequency')}
          options={frequencyOptions}
          selectedValue={String(selectedPlanId)}
          onChange={handleFrequencyChange}
          onSave={handleFrequencySave}
          onClose={handleFrequencyClose}
          saveButtonText={t('save')}
          className={styles.frequencySelectorContainer}
        />
      </PostOrderProviderWrapper>
    </div>
  )
}

export default PurchaseTypeSelector
